import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Loader2, Mail, ArrowUpRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { API } from "../context/SiteContext";

export default function AdminForgotPassword() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Enter the email on file.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: email.trim().toLowerCase() });
      setSent(true);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Try again.";
      toast.error(typeof msg === "string" ? msg : "Something went wrong. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#FDFBF7] flex items-center justify-center px-6 py-16">
      <div className="w-full max-w-md">
        <div className="mb-12 text-center">
          <p className="overline text-[#9F5C45] mb-4">Password reset</p>
          <h1 className="font-serif font-light text-[#1C1917] text-4xl md:text-5xl tracking-tight">
            Reset your password.
          </h1>
          <p className="text-[#57534E] mt-4">
            Enter the supervisor email on file. We'll send a one-time link to set a new password.
          </p>
        </div>

        {sent ? (
          <div
            data-testid="forgot-password-sent"
            className="bg-white border border-[#E2DCD0] p-10 text-center space-y-4"
          >
            <CheckCircle2 className="w-10 h-10 text-[#3A5A40] mx-auto" />
            <h2 className="font-serif text-2xl text-[#1C1917]">Check your inbox.</h2>
            <p className="text-sm text-[#57534E] leading-relaxed">
              If <span className="font-medium text-[#1C1917]">{email}</span> is registered, a reset
              link is on the way. It expires in 30 minutes and can only be used once.
            </p>
            <Link
              to="/admin"
              data-testid="forgot-password-back-to-login"
              className="inline-flex items-center gap-2 text-sm text-[#3A5A40] hover:text-[#1C1917] transition-colors pt-2"
            >
              ← Back to sign in
            </Link>
          </div>
        ) : (
          <form
            onSubmit={submit}
            className="bg-white border border-[#E2DCD0] p-10 space-y-7"
            data-testid="forgot-password-form"
          >
            <label className="block">
              <span className="overline text-[#57534E] mb-2 block">Email</span>
              <input
                data-testid="forgot-password-email"
                type="email"
                className="lgw-field"
                placeholder="letgowellness1@gmail.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoFocus
              />
            </label>

            <button
              type="submit"
              disabled={submitting}
              data-testid="forgot-password-submit"
              className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 btn-primary text-sm tracking-wide disabled:opacity-60"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Sending link
                </>
              ) : (
                <>
                  <Mail className="w-4 h-4" /> Email me a reset link <ArrowUpRight className="w-4 h-4" />
                </>
              )}
            </button>

            <p className="text-xs text-[#57534E] text-center pt-2">
              Link expires in 30 minutes · one-time use
            </p>
          </form>
        )}

        <p className="text-center mt-8">
          <Link to="/admin" className="text-sm text-[#57534E] hover:text-[#3A5A40] transition-colors">
            ← Back to sign in
          </Link>
        </p>
      </div>
    </main>
  );
}
